import React from 'react';
import { Button, Grid, Icon, Label, Search } from 'semantic-ui-react';
import { Field, reduxForm, getFormValues } from 'redux-form';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { toastError } from 'Common/ToastHelper';
import MPWApi from 'API/MPWApi';
import { ING_PURCHASING_FORM_NAME, InputMode } from 'Common/FormEnum';
import { IngPurchasingFieldEnum as Fields } from 'Common/FieldEnum';
import { InputField, Select } from 'Common/SemanticWrapper';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import {
  getAcctCategoryForCurrentSite,
  getVendorForCurrentSite,
} from 'State/filters/actions';
import { getAccessGroupEnabled } from 'Common/Security/state/selectors';

import {
  selectVendorOptions,
  getVendorDataLoading,
  selectAcctCategoryOptions,
  getAcctCategoryDataLoading,
} from 'State/filters/selectors';
import { NOTIFICATION_DIALOG_MESSAGES } from 'Common/Constants';
import { DEFAULT_ACCOUNT_CATEGORY_ID } from 'Common/Constants/inventory';
import { WEIGHT_UNIT_OPTIONS } from 'Pages/manageingredient/helpers/Constants';

class SearchDropdown extends React.Component {
  static INITIAL_STATE = {
    value: '',
    loading: false,
    results: [],
  };

  static propTypes = {
    handleResultSelect: PropTypes.func,
    minLength: PropTypes.number, // min length before a search is triggered
  };

  static defaultProps = {
    handleResultSelect: (value) => {},
    minLength: 0,
  };

  state = SearchDropdown.INITIAL_STATE;

  handleSearchChange = (e, { value }) => {
    const { minLength } = this.props;
    const trimmedValue = _.trim(value);
    this.setState(
      { value, loading: trimmedValue.length >= minLength },
      this.debouncedSearch
    );
  };

  search = async () => {
    const nextState = {
      results: [],
      loading: false,
    };
    try {
      const { minLength } = this.props;
      const { value } = this.state;
      const trimmedValue = _.trim(value);
      this.setState({ loading: true });

      if (trimmedValue.length >= minLength) {
        const response = await MPWApi.postGenericInsertUpdate(
          '/INVItem/GetInvItemSearchResults',
          {
            Search: trimmedValue,
          }
        );
        const data = response.data.Data;
        nextState.results = data;
      }
    } catch (ex) {
      toastError('There was an issue retrieving inventory items.');
    } finally {
      this.setState(nextState);
    }
  };

  debouncedSearch = _.debounce(this.search, 300);

  handleResultSelect = (e, { result }) => {
    this.setState({ value: '', data: [] });
    this.props.onResultSelect(result);
  };

  resultRenderer(result) {
    return (
      <div key={result.Id} className="result-container">
        <label>
          {result.ItemCode} - {result.Description}
        </label>
        <Button floated="right" basic animated="vertical">
          <Button.Content visible>Link</Button.Content>
          <Button.Content hidden>
            <Icon name="linkify" />
          </Button.Content>
        </Button>
      </div>
    );
  }

  render() {
    const { results, loading, value } = this.state;
    const {
      onResultSelect,
      minLength,
      handleResultSelect,
      ...rest
    } = this.props;

    let noResultsMessage = 'No results found.';
    let noResultsDescription =
      value.length > 0 && value.length < minLength
        ? `Please enter at least ${minLength} characters.`
        : '';

    if (loading) {
      noResultsDescription = '';
      noResultsMessage = 'Loading...';
    }

    return (
      <Search
        input={{ icon: 'search', iconPosition: 'left' }}
        fluid
        loading={loading}
        onResultSelect={this.handleResultSelect}
        onSearchChange={this.handleSearchChange}
        results={results}
        value={value}
        resultRenderer={this.resultRenderer}
        noResultsDescription={noResultsDescription}
        noResultsMessage={noResultsMessage}
        {...rest}
      />
    );
  }
}

class InventoryPurchasingUnitForm extends React.Component {
  state = {
    // if has inventory linked, show Link to Inventory, otherwise show manual tab first
    buttonGroupOption: this.props.formValues[Fields.InvItem] ? 2 : 1,
    inventoryItem: null,
  };

  buttonGroupOptions = [
    {
      label: 'Manual Entry',
      value: 1,
    },
    {
      label: 'Link to Inventory',
      value: 2,
    },
  ];

  componentDidMount() {
    const {
      getAcctCategoryForCurrentSite,
      getVendorForCurrentSite,
      inventoryEnabled,
    } = this.props;
    if (!inventoryEnabled) {
      delete this.buttonGroupOptions[1];
    }
    getVendorForCurrentSite();
    getAcctCategoryForCurrentSite();
  }

  handleOptionChange = (e, option) =>
    this.setState({ buttonGroupOption: option });

  onResultSelect = (value) => {
    const { change } = this.props;
    change(Fields.INV_ItemId, value.Id);
    change(Fields.InvItem, value);

    change(Fields.PrimaryPrice, value[Fields.PrimaryPrice]);
    change(Fields.Unit, value[Fields.Unit]);
    change(Fields.QtyPerUnit, value[Fields.QtyPerUnit]);
    change(Fields.SecondaryUnit, value[Fields.SecondaryUnit]);
    change(Fields.SecondaryUnitWeight, value[Fields.SecondaryUnitWeight]);
    change(Fields.SecondaryUnitMeasure, value[Fields.SecondaryUnitMeasure]);
    change(Fields.AccountCategoryId, value[Fields.AccountCategoryId]);
    change(Fields.VendorId, value[Fields.VendorId]);
  };

  unlinkInvItem = () => {
    const { change, setNotificationDialog } = this.props;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.MENU_UNLINK_INVENTORY_ITEM,
      functionsToRunOnOk: [
        () => {
          change(Fields.INV_ItemId, '');
          change(Fields.InvItem, '');
          this.resetPurchasing();
        },
      ],
    });
  };

  resetManualEntry = () => {
    const { setNotificationDialog } = this.props;
    setNotificationDialog({
      ...NOTIFICATION_DIALOG_MESSAGES.MENU_RESET_PURCHASING_UNIT_FORM,
      functionsToRunOnOk: [this.resetPurchasing],
    });
  };

  resetPurchasing = () => {
    const { change } = this.props;
    change(Fields.PrimaryPrice, '');
    change(Fields.Unit, '');
    change(Fields.QtyPerUnit, '');
    change(Fields.SecondaryUnit, '');
    change(Fields.SecondaryUnitWeight, '');
    change(Fields.SecondaryUnitMeasure, '');
    change(Fields.AccountCategoryId, DEFAULT_ACCOUNT_CATEGORY_ID);
    change(Fields.VendorId, '');
  };

  get purchasingFieldsHaveValue() {
    const { formValues } = this.props;
    return Boolean(
      formValues[Fields.PrimaryPrice] ||
        formValues[Fields.Unit] ||
        formValues[Fields.QtyPerUnit] ||
        formValues[Fields.SecondaryUnit] ||
        formValues[Fields.SecondaryUnitWeight] ||
        formValues[Fields.SecondaryUnitMeasure]
    );
  }

  renderInputs() {
    const {
      vendorOptions,
      vendorOptionsLoading,
      accountCategoryOptions,
      accountCategoryOptionsLoading,
      isReadOnly,
      isInventoryLicensed,
    } = this.props;

    return (
      <Grid padded="vertically">
        <Grid.Row>
          <Grid.Column width="11" />
          <Grid.Column width="5" textAlign="center">
            <label>Pack Unit Weight</label>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row textAlign="center">
          <Grid.Column width="3">
            <Field
              label="Purchase Unit Price"
              name={Fields.PrimaryPrice}
              component={InputField}
              numericSize={[7, 4]}
              fluid
              mode={InputMode.Decimal}
              icon={<Icon name="dollar" />}
              iconPosition="left"
              disabled={isReadOnly}
            />
          </Grid.Column>
          <Grid.Column width="3">
            <Field
              label="Purchase Unit"
              name={Fields.Unit}
              component={InputField}
              maxLength={15}
              fluid
              disabled={isReadOnly}
            />
          </Grid.Column>
          <Grid.Column width="2">
            <Field
              label="Pack Size"
              name={Fields.QtyPerUnit}
              fluid
              component={InputField}
              maxLength={15}
              mode={InputMode.Integer}
              disabled={isReadOnly}
            />
          </Grid.Column>
          <Grid.Column width="3">
            <Field
              label="Pack Unit"
              name={Fields.SecondaryUnit}
              fluid
              component={InputField}
              maxLength={15}
              disabled={isReadOnly}
            />
          </Grid.Column>
          <Grid.Column width="2">
            <Field
              label="Amount"
              name={Fields.SecondaryUnitWeight}
              fluid
              component={InputField}
              mode={InputMode.Decimal}
              numericSize={[10, 2]}
              disabled={isReadOnly}
            />
          </Grid.Column>
          <Grid.Column width="3">
            <Field
              label="Measure"
              name={Fields.SecondaryUnitMeasure}
              component={Select}
              usePortal={true}
              fluid
              options={WEIGHT_UNIT_OPTIONS}
              disabled={isReadOnly}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row columns={2}>
          <Grid.Column>
            <Field
              label="Account Category"
              name={Fields.AccountCategoryId}
              component={Select}
              usePortal={true}
              fluid
              options={accountCategoryOptions}
              loading={accountCategoryOptionsLoading}
              disabled={isReadOnly}
            />
          </Grid.Column>
          <Grid.Column>
            {isInventoryLicensed && (
              <Field
                label="Distributor"
                name={Fields.VendorId}
                component={Select}
                usePortal={true}
                fluid
                options={vendorOptions}
                loading={vendorOptionsLoading}
                disabled={isReadOnly}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  get note() {
    const { formValues } = this.props;
    const invItem = formValues[Fields.InvItem];
    const hasInvItemLinked = Boolean(invItem);
    let note = '';

    if (hasInvItemLinked) {
      note =
        'Note: An inventory item has been linked to this ingredient, please unlink to allow manual entry.';
    }

    if (hasInvItemLinked) {
      note = 'Note: An inventory item has been linked to this ingredient.';

      if (!this.purchasingFieldsHaveValue) {
        note +=
          ' Please save to complete linking an inventory item to this ingredient.';
      }
    }

    if (!hasInvItemLinked && this.purchasingFieldsHaveValue) {
      note =
        'Note: An inventory item cannot be linked until the manually entered fields are reset.';
    }

    return note;
  }

  render() {
    const { formValues, inventoryEnabled, isReadOnly } = this.props;
    const invItem = formValues[Fields.InvItem];
    const hasInvItemLinked = Boolean(invItem);

    return (
      <React.Fragment>
        <Grid padded="vertically">
          {inventoryEnabled && !isReadOnly && (
            <Grid.Row>
              {hasInvItemLinked && (
                <Grid.Column width={16}>
                  <React.Fragment>
                    <Label basic pointing="left">
                      <Icon name="linkify" />
                      {invItem.ItemCode} - {invItem.Description}
                    </Label>{' '}
                    <Button
                      onClick={this.unlinkInvItem}
                      animated="vertical"
                      color="red"
                    >
                      <Button.Content visible>Unlink</Button.Content>
                      <Button.Content hidden>
                        <Icon name="unlinkify" />
                      </Button.Content>
                    </Button>
                  </React.Fragment>
                </Grid.Column>
              )}
              {!hasInvItemLinked && (
                <React.Fragment>
                  <Grid.Column width={8}>
                    <SearchDropdown
                      disabled={this.purchasingFieldsHaveValue}
                      onResultSelect={this.onResultSelect}
                      minLength={3}
                      placeholder="Inventory Smart Search ex. Chicken"
                    />
                  </Grid.Column>
                  <Grid.Column width={8}>
                    {!hasInvItemLinked && this.purchasingFieldsHaveValue && (
                      <Button
                        color="red"
                        floated="right"
                        onClick={this.resetManualEntry}
                      >
                        Reset Manual Entry
                      </Button>
                    )}
                  </Grid.Column>
                </React.Fragment>
              )}
            </Grid.Row>
          )}
        </Grid>
        <Grid padded="vertically" className="inventory-purchasing-unit-grid">
          <Grid.Row columns="1">
            <Grid.Column>{this.renderInputs()}</Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid>
          <Grid.Row>
            <Grid.Column width="12">
              <p>{this.note}</p>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </React.Fragment>
    );
  }
}

const validate = (values) => {
  const errors = {};
  const validateIngPurchasingFields = [
    Fields.PrimaryPrice,
    Fields.Unit,
    Fields.QtyPerUnit,
    Fields.SecondaryUnit,
    Fields.SecondaryUnitMeasure,
    Fields.SecondaryUnitWeight,
    Fields.VendorId,
  ];

  const purchasingFieldsHaveValues = validateIngPurchasingFields.some(
    (field) => values[field]
  );

  if (purchasingFieldsHaveValues) {
    validateIngPurchasingFields.forEach((field) => {
      if (!values[field]) {
        errors[field] = 'Required';
      }
    });
  }

  return errors;
};

InventoryPurchasingUnitForm = reduxForm({
  form: ING_PURCHASING_FORM_NAME,
  destroyOnUnmount: false,
  validate,
})(InventoryPurchasingUnitForm);

const formValueSelector = getFormValues(ING_PURCHASING_FORM_NAME);
const mapStateToProps = (state) => ({
  formValues: formValueSelector(state) || {},
  vendorOptions: selectVendorOptions(state),
  vendorOptionsLoading: getVendorDataLoading(state),
  accountCategoryOptions: selectAcctCategoryOptions(state),
  accountCategoryOptionsLoading: getAcctCategoryDataLoading(state),
  inventoryEnabled: getAccessGroupEnabled(state, 'Inventory'),
});

const mapDispatchToProps = {
  setNotificationDialog,
  getVendorForCurrentSite,
  getAcctCategoryForCurrentSite,
};

InventoryPurchasingUnitForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(InventoryPurchasingUnitForm);

export { InventoryPurchasingUnitForm };



// WEBPACK FOOTER //
// ./src/pages/manageingredient/subpages/editingredient/components/InventoryPurchasingUnitForm.js